/**
 * ISO 3166-1 alpha-2 → primary international airport IATA mapping.
 *
 * Used by the add-choice flight CTA when we know the user's home country
 * or the trip's destination country but not a specific airport. The picks
 * are the dominant international gateway per country — accurate enough
 * to land the user on a useful Aviasales search; they can correct origin
 * on the partner page when their actual home airport differs (a US user
 * in San Francisco can swap JFK → SFO with two taps).
 *
 * Coverage is intentionally finite (~80 countries). When the input
 * country code is unmapped, callers fall back to the marker-only
 * Aviasales browse URL via `buildFlightBrowseUrl`.
 */

const COUNTRY_TO_PRIMARY_IATA: Record<string, string> = {
  // Americas
  US: 'JFK',
  CA: 'YYZ',
  MX: 'MEX',
  BR: 'GRU',
  AR: 'EZE',
  CL: 'SCL',
  CO: 'BOG',
  PE: 'LIM',
  VE: 'CCS',
  UY: 'MVD',
  EC: 'UIO',
  BO: 'VVI',
  PY: 'ASU',
  PA: 'PTY',
  CR: 'SJO',
  GT: 'GUA',
  DO: 'SDQ',
  CU: 'HAV',
  JM: 'KIN',
  TT: 'POS',
  // Europe
  GB: 'LHR',
  IE: 'DUB',
  FR: 'CDG',
  DE: 'FRA',
  IT: 'FCO',
  ES: 'MAD',
  PT: 'LIS',
  NL: 'AMS',
  BE: 'BRU',
  LU: 'LUX',
  CH: 'ZRH',
  AT: 'VIE',
  SE: 'ARN',
  NO: 'OSL',
  DK: 'CPH',
  FI: 'HEL',
  IS: 'KEF',
  PL: 'WAW',
  CZ: 'PRG',
  SK: 'BTS',
  HU: 'BUD',
  RO: 'OTP',
  BG: 'SOF',
  GR: 'ATH',
  HR: 'ZAG',
  SI: 'LJU',
  RS: 'BEG',
  AL: 'TIA',
  MT: 'MLA',
  CY: 'LCA',
  EE: 'TLL',
  LV: 'RIX',
  LT: 'VNO',
  UA: 'KBP',
  RU: 'SVO',
  BY: 'MSQ',
  TR: 'IST',
  // Middle East
  AE: 'DXB',
  SA: 'JED',
  QA: 'DOH',
  KW: 'KWI',
  BH: 'BAH',
  OM: 'MCT',
  IL: 'TLV',
  JO: 'AMM',
  LB: 'BEY',
  // Africa
  EG: 'CAI',
  MA: 'CMN',
  TN: 'TUN',
  ZA: 'JNB',
  KE: 'NBO',
  NG: 'LOS',
  ET: 'ADD',
  GH: 'ACC',
  TZ: 'DAR',
  MU: 'MRU',
  // Asia
  JP: 'NRT',
  CN: 'PEK',
  HK: 'HKG',
  TW: 'TPE',
  KR: 'ICN',
  SG: 'SIN',
  MY: 'KUL',
  TH: 'BKK',
  VN: 'SGN',
  ID: 'CGK',
  PH: 'MNL',
  IN: 'DEL',
  PK: 'KHI',
  BD: 'DAC',
  LK: 'CMB',
  NP: 'KTM',
  KH: 'PNH',
  MM: 'RGN',
  KZ: 'ALA',
  UZ: 'TAS',
  // Oceania
  AU: 'SYD',
  NZ: 'AKL',
  FJ: 'NAN',
};

/**
 * Look up the primary international airport IATA for a country code.
 * Returns null when the country isn't in the mapping or the input
 * isn't a 2-letter alpha code. Input is case-insensitive.
 */
export function countryToPrimaryIata(countryCode: string | undefined): string | null {
  if (!countryCode) return null;
  const code = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  return COUNTRY_TO_PRIMARY_IATA[code] ?? null;
}
